import { Client } from '../utils/client';

// Base URL for eSIM public API
// If localhost, use CloudFront URL
// If production, use the main domain
const getBaseURL = () => {
  const hostname = window.location.hostname;
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0';
  
  if (isLocalhost) {
    // Use CloudFront URL for localhost
    return 'https://dqm23rgxerxzo.cloudfront.net';
  } else {
    // Use main domain for UAT/PROD
    return window.location.origin;
  }
};

/**
 * Initialize API client with base URL
 */
export const initAPIClient = async () => {
  try {
    const baseURL = getBaseURL();
    await Client.setBaseURL(baseURL);
    // console.log('API base URL set to:', baseURL);
    // console.log('Current hostname:', window.location.hostname);
  } catch (error) {
    // console.error('Error setting API base URL:', error);
    throw error;
  }
};

/**
 * Get all products
 * @param {string} language - Language code (e.g., 'EN')
 * @param {string} region - Region code (e.g., 'SG')
 * @param {boolean} promoOnly - Whether to fetch promo-only products (default: true)
 * @returns {Promise} API response
 */
export const getAllProducts = async (language = 'EN', region = 'SG', promoOnly = true) => {
  try {
    let url = `/api/web/order/purchase/product?language=${language}&region=${region}`;
    if (promoOnly) {
      url += '&promoOnly=true';
    }
    const response = await Client.getData(url);
    return response;
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

/**
 * Apply promo code
 * @param {string[]} productMasterUuids - Array of product master UUIDs
 * @param {string} promoCode - Promo code to apply
 * @returns {Promise} API response
 */
export const applyPromoCode = async (productMasterUuids, promoCode) => {
  try {
    const url = '/api/v1/order/promo-code/apply';
    const body = {
      productMasterUuids: Array.isArray(productMasterUuids) ? productMasterUuids : [productMasterUuids],
      promoCode
    };
    const baseURL = getBaseURL();
    // console.log('Applying promo code - Full URL will be:', baseURL + url);
    // console.log('Request body:', body);
    const response = await Client.postData(url, body);
    // console.log('Promo code response:', response);
    return response;
  } catch (error) {
    // console.error('Error applying promo code:', error);
    // console.error('Error details:', {
    //   message: error?.message,
    //   response: error?.response?.data,
    //   status: error?.response?.status,
    //   url: error?.config?.url,
    //   baseURL: error?.config?.baseURL,
    //   fullURL: error?.config?.baseURL + error?.config?.url
    // });
    throw error;
  }
};

/**
 * Submit purchase
 * @param {Object} purchaseData - Purchase data
 * @param {string[]} purchaseData.productMasterUuids - Array of product master UUIDs
 * @param {string} purchaseData.lastname - Last name
 * @param {string} purchaseData.firstname - First name
 * @param {string} purchaseData.email - Email address
 * @param {string} [purchaseData.promoCode] - Optional promo code
 * @returns {Promise} API response
 */
export const submitPurchase = async (purchaseData) => {
  try {
    const url = '/api/web/order/purchase/submitPurchase';
    const body = {
      lastname: purchaseData.lastname,
      firstname: purchaseData.firstname,
      email: purchaseData.email,
      productMasterUuids: purchaseData.productMasterUuids,
      ...(purchaseData.promoCode && { promoCode: purchaseData.promoCode })
    };
    const response = await Client.postData(url, body);
    return response;
  } catch (error) {
    // console.error('Error submitting purchase:', error);
    throw error;
  }
};

/**
 * Verify email OTP
 * @param {string} transactionId - Transaction ID
 * @param {string} otp - OTP code
 * @returns {Promise} API response
 */
export const verifyEmailOTP = async (transactionId, otp) => {
  try {
    const url = '/api/web/order/purchase/verifyEmailOTP';
    const body = {
      transactionId,
      otp
    };
    const response = await Client.postData(url, body);
    return response;
  } catch (error) {
    // console.error('Error verifying email OTP:', error);
    throw error;
  }
};

/**
 * Resend email OTP
 * @param {string} transactionId - Transaction ID
 * @returns {Promise} API response
 */
export const resendEmailOTP = async (transactionId) => {
  try {
    const url = '/api/web/order/purchase/resendEmailOtp';
    const body = {
      transactionId
    };
    const response = await Client.postData(url, body);
    return response;
  } catch (error) {
    // console.error('Error resending email OTP:', error);
    throw error;
  }
};

/**
 * Get promo-only product (for redeem page)
 * @param {string} language - Language code (e.g., 'EN')
 * @param {string} region - Region code (e.g., 'SG')
 * @returns {Promise} API response with promo product data
 */
export const getPromoProduct = async (language = 'EN', region = 'SG') => {
  try {
    const url = `/api/web/product?language=${language}&region=${region}&promoOnly=true`;
    const response = await Client.getData(url);
    return response;
  } catch (error) {
    console.error('Error fetching promo product:', error);
    throw error;
  }
};

/**
 * Get FAQ data
 * @returns {Promise} API response with FAQ data
 */
export const getFAQData = async () => {
  try {
    const url = '/api/web/resource?type=faq';
    const response = await Client.getData(url);
    return response;
  } catch (error) {
    console.error('Error fetching FAQ data:', error);
    throw error;
  }
};

